import React from 'react';
import { bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import arrayMutators from 'final-form-arrays';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import config from '../../config';
import { LINE_ITEM_NIGHT, LINE_ITEM_DAY, propTypes } from '../../util/types';
import * as validators from '../../util/validators';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import { subUnitDivisors } from '../../currency-config';
import {
  PrimaryButtonInline,
  Form,
  FieldCurrencyInput,
  FieldTextInput,
  NamedLink,
  PreviewListingButton,
  FieldSelect,
  FieldRadioButton,
  InlineTextButton,
  Icon,
} from '../../components';
import TourScheduleField from './TourScheduleField';

import css from './EditListingTouringLinksForm.module.css';

const { Money } = sdkTypes;

const getSupportedCurrencies = () => Object.keys(subUnitDivisors).map(c => c);

export const EditListingTouringLinksFormComponent = props => (
  <FinalForm
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        disabled,
        ready,
        handleSubmit,
        intl,
        invalid,
        pristine,
        values,
        errors,
        saveActionMsg,
        updated,
        updateInProgress,
        fetchErrors,
        panelTitle,
        listing,
        onGetPrevPathParams,
      } = formRenderProps;

      const prevPathParams = onGetPrevPathParams();

      const unitType = config.bookingUnitType;
      const isNightly = unitType === LINE_ITEM_NIGHT;
      const isDaily = unitType === LINE_ITEM_DAY;

      const translationKey = isNightly
        ? 'EditListingTouringLinksForm.pricePerNight'
        : isDaily
        ? 'EditListingTouringLinksForm.pricePerDay'
        : 'EditListingTouringLinksForm.pricePerUnit';

      const pricePerUnitMessage = intl.formatMessage({
        id: translationKey,
      });

      const pricePlaceholderMessage = intl.formatMessage({
        id: 'EditListingTouringLinksForm.priceInputPlaceholder',
      });

      const priceRequired = validators.required(
        intl.formatMessage({
          id: 'EditListingTouringLinksForm.priceRequired',
        })
      );
      const minPrice = new Money(config.listingMinimumPriceSubUnits, config.currency);
      const minPriceRequired = validators.moneySubUnitAmountAtLeast(
        intl.formatMessage(
          {
            id: 'EditListingTouringLinksForm.priceTooLow',
          },
          {
            minPrice: formatMoney(intl, minPrice),
          }
        ),
        config.listingMinimumPriceSubUnits
      );
      const priceValidators = config.listingMinimumPriceSubUnits
        ? validators.composeValidators(priceRequired, minPriceRequired)
        : priceRequired;

      const touringLinkMessage = intl.formatMessage({
        id: 'EditListingTouringLinksForm.touringLink',
      });
      const touringLinkPlaceholderMessage = intl.formatMessage({
        id: 'EditListingTouringLinksForm.touringLinkPlaceholder',
      });
      const touringLinkValidMessage = intl.formatMessage({
        id: 'EditListingTouringLinksForm.validLink',
      });
      const touringLinkValidURL = validators.validURL(touringLinkValidMessage);

      const availabilityStatusMessage = intl.formatMessage({
        id: 'EditListingTouringLinksForm.availabilityStatus',
      });

      const classes = classNames(css.root, className);
      const submitReady = (updated && pristine) || ready;
      const submitInProgress = updateInProgress;
      const submitDisabled = invalid || disabled || submitInProgress;
      const { updateListingError, showListingsError, publishListingError } = fetchErrors || {};

      // NOTE: These error messages are here since Photos panel is the last visible panel
      // before creating a new listing. If that order is changed, these should be changed too.
      // Create and show listing errors are shown above submit button
      const publishListingFailed = publishListingError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingGalleryForm.publishListingFailed" />
        </p>
      ) : null;
      const showListingFailed = showListingsError ? (
        <p className={css.error}>
          <FormattedMessage id="EditListingGalleryForm.showListingFailed" />
        </p>
      ) : null;

      return (
        <Form onSubmit={handleSubmit} className={classes}>
          <div className={css.content}>
            <div className={css.contentWrapper}>
              <h1 className={css.title}>{panelTitle}</h1>
              {updateListingError ? (
                <p className={css.error}>
                  <FormattedMessage id="EditListingTouringLinksForm.updateFailed" />
                </p>
              ) : null}
              {showListingsError ? (
                <p className={css.error}>
                  <FormattedMessage id="EditListingTouringLinksForm.showListingFailed" />
                </p>
              ) : null}
              {publishListingFailed}
              {showListingFailed}

              <div className={css.seatPrice}>
                <label>{pricePerUnitMessage}</label>
                <div className={css.priceInputs}>
                  <FieldCurrencyInput
                    id="price"
                    name="price"
                    className={css.priceInput}
                    placeholder={pricePlaceholderMessage}
                    currencyConfig={{
                      ...config.currencyConfig,
                      currency: values.currency,
                    }}
                    validate={priceValidators}
                  />
                  <FieldSelect id="currency" name="currency">
                    {getSupportedCurrencies().map(c => {
                      return (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      );
                    })}
                  </FieldSelect>
                </div>
                {errors.price && values.price ? (
                  <p className={css.priceError}>{errors.price}</p>
                ) : null}
              </div>

              <FieldTextInput
                id="touringLink"
                name="touringLink"
                className={css.field}
                type="text"
                label={touringLinkMessage}
                placeholder={touringLinkPlaceholderMessage}
                validate={touringLinkValidURL}
              />

              <fieldset className={css.field}>
                <legend>{availabilityStatusMessage}</legend>
                {[
                  [
                    'available',
                    intl.formatMessage({ id: 'EditListingTouringLinksForm.available' }),
                  ],
                  [
                    'notAvailable',
                    intl.formatMessage({ id: 'EditListingTouringLinksForm.notAvailable' }),
                  ],
                  [
                    'collectWaitlist',
                    intl.formatMessage({ id: 'EditListingTouringLinksForm.collectWaitlist' }),
                  ],
                ].map(([status, label]) => (
                  <FieldRadioButton
                    key={status}
                    className={css.availabilityStatusItem}
                    id={`availabilityStatus.${status}`}
                    name="availabilityStatus"
                    value={status}
                    label={label}
                  />
                ))}
              </fieldset>

              <div className={css.tourSchedulesSection}>
                <label className={css.tourSchedulesTitle}>
                  <FormattedMessage id="EditListingTouringLinksForm.tourSchedulesTitle" />
                </label>

                <FieldArray name="tourSchedules">
                  {({ fields }) => (
                    <div className={css.tourSchedulesList}>
                      {fields.map((name, index) => {
                        const canRemove = true; // Always allow removal since tour schedules are optional
                        const isLastItem = index === fields.length - 1;
                        const shouldAutoFocus = isLastItem && fields.length > 0;
                        return (
                          <TourScheduleField
                            key={name}
                            name={name}
                            index={index}
                            canRemove={canRemove}
                            onRemove={() => fields.remove(index)}
                            form={formRenderProps}
                            autoFocus={shouldAutoFocus}
                          />
                        );
                      })}

                      {fields.length === 0 && (
                        <p className={css.noSchedulesMessage}>
                          <FormattedMessage id="EditListingTouringLinksForm.noSchedulesMessage" />
                        </p>
                      )}

                      {fields.length < 10 && (
                        <InlineTextButton
                          type="button"
                          className={css.addTourButton}
                          onClick={() =>
                            fields.push({
                              location: null,
                              startDate: null,
                              endDate: null,
                            })
                          }
                        >
                          <Icon name="add" className={css.addIcon} />
                          <FormattedMessage id="EditListingTouringLinksForm.addTourSchedule" />
                        </InlineTextButton>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>
          </div>

          <div className={css.submitButtonRoot}>
            <NamedLink className={css.backButton} name="EditListingPage" params={prevPathParams}>
              <FormattedMessage id="EditListingGalleryForm.backButton" />
            </NamedLink>
            <div className={css.submitAndPreviewButton}>
              <PreviewListingButton listing={listing} />
              <PrimaryButtonInline
                size="small"
                checkmarkSize="small"
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </PrimaryButtonInline>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

EditListingTouringLinksFormComponent.defaultProps = { fetchErrors: null };

EditListingTouringLinksFormComponent.propTypes = {
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
};

export default compose(injectIntl)(EditListingTouringLinksFormComponent);
